import {
  AudioThumbnail,
  DocThumbnail,
  RemoveFileIcon,
  SendIconButton,
  SendIconMui,
  ThumbImage,
  ThumbImagesContainer,
  ThumbnailContainer,
  VideoThumbnail,
} from "./Preview.style";
import { useEffect, useState } from "react";
import AddFileComponent from "./AddFileComponent";
import { getFileParams } from "./FileUtils";
import { IFileTypeComponentMap } from "./Preview";
import {
  FILE_TYPE,
  fileType,
  IFile,
  IMediaFile,
} from "@home-health-notify/domain";

interface IThumbImages {
  files: IMediaFile[];
  openFileDialog: () => void;
  previewFile: IMediaFile | undefined;
  onSetPreview: (file: IMediaFile) => void;
  onRemoveFile: (file: IMediaFile) => void;
  upload: () => Promise<void>;
  isThumbnailCreated: boolean;
  resetThumbnailFlag: () => void;
}

export const ThumbImages = ({
  files,
  openFileDialog,
  previewFile,
  onSetPreview,
  onRemoveFile,
  upload,
  isThumbnailCreated,
  resetThumbnailFlag,
}: IThumbImages) => {
  return (
    <ThumbImagesContainer>
      {files.map((file, index) => (
        <ThumbnailsComponent
          key={`${file.name}-${file.size}-${index}`}
          file={file}
          isSelected={previewFile?.name === file.name}
          onSetPreview={onSetPreview}
          onRemoveFile={onRemoveFile}
          isThumbnailCreated={isThumbnailCreated}
          resetThumbnailFlag={resetThumbnailFlag}
        />
      ))}
      <AddFileComponent openFileDialog={openFileDialog} />
      <SendIconButton
        data-testid="upload-files"
        color="primary"
        aria-label="edit"
        onClick={upload}
      >
        <SendIconMui />
      </SendIconButton>
    </ThumbImagesContainer>
  );
};

interface IThumbnailsComponent {
  file: IMediaFile;
  isSelected: boolean;
  onSetPreview: (file: IMediaFile) => void;
  onRemoveFile: (file: IMediaFile) => void;
  isThumbnailCreated: boolean;
  resetThumbnailFlag: () => void;
}

const ThumbnailsComponent = ({
  file,
  isSelected,
  onSetPreview,
  onRemoveFile,
  isThumbnailCreated,
  resetThumbnailFlag,
}: IThumbnailsComponent) => {
  const [isHover, setHover] = useState(false);
  const FileTypeComponentMap: IFileTypeComponentMap = {
    [FILE_TYPE.IMAGE]: ImageThumbnailComponent,
    [FILE_TYPE.VIDEO]: VideoThumbnailComponent,
    [FILE_TYPE.AUDIO]: AudioThumbnailComponent,
    [FILE_TYPE.DOC]: DocThumbnailComponent,
  };

  let fileParams = getFileParams(file);

  let ThumbnailFileTypeComponent = FileTypeComponentMap[fileParams.fileType];

  return (
    <ThumbnailContainer
      isSelected={isSelected}
      data-testid="thumb-image"
      onClick={() => onSetPreview(file)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <ThumbnailFileTypeComponent
        fileParams={fileParams}
        iconPath={fileParams.iconPath}
        fileName={fileParams.fileName}
        fileSize={fileParams.fileSize}
        filePath={fileParams.filePath}
        getThumbnail={fileParams.getThumbnail}
        videoPreview={fileParams.videoPreview}
        isThumbnailCreated={isThumbnailCreated}
        resetThumbnailFlag={resetThumbnailFlag}
      />
      {(isSelected || isHover) && (
        <RemoveFileIcon
          data-testid="remove-file"
          onClick={e => {
            e.stopPropagation();
            onRemoveFile(file);
          }}
        />
      )}
      {fileType(file) === FILE_TYPE.VIDEO && (
        <VideoThumbnail fontSize="large" />
      )}
    </ThumbnailContainer>
  );
};

const ImageThumbnailComponent = ({
  getThumbnail,
}: {
  getThumbnail: () => Promise<IFile>;
}) => {
  const [thumbnailSrc, setThumbnailSrc] = useState<string>();

  useEffect(() => {
    const loadThumbnailSrc = async () => {
      const thumbnail = await getThumbnail();
      setThumbnailSrc(thumbnail.filePath);
    };
    loadThumbnailSrc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoad = async (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const thumbnail = await getThumbnail();
    thumbnail.setDimension(img.naturalWidth, img.naturalHeight);
  };

  return <ThumbImage src={thumbnailSrc} onLoad={onLoad} />;
};

interface IVideoThumbnailComponent {
  getThumbnail: () => Promise<IFile>;
  isThumbnailCreated: boolean;
  resetThumbnailFlag: () => void;
}

const VideoThumbnailComponent = ({
  getThumbnail,
  isThumbnailCreated,
  resetThumbnailFlag,
}: IVideoThumbnailComponent) => {
  const [preview, setPreview] = useState<IFile>();
  useEffect(() => {
    (async () => {
      if (isThumbnailCreated) {
        setPreview(await getThumbnail());
        resetThumbnailFlag();
      }
    })();
    // eslint-disable-next-line
  }, [isThumbnailCreated]);

  const onLoad = async (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const thumbnail = await getThumbnail();
    thumbnail.setDimension(img.naturalWidth, img.naturalHeight);
  };

  return preview ? (
    <ThumbImage src={preview.filePath} onLoad={onLoad} />
  ) : (
    <VideoThumbnail fontSize="large" />
  );
};

const AudioThumbnailComponent = () => {
  return <AudioThumbnail fontSize="large" />;
};

interface IDocThumbnailComponent {
  iconPath?: string;
  fileName?: string;
}

export const DocThumbnailComponent = ({
  iconPath,
  fileName,
}: IDocThumbnailComponent) => {
  return (
    <DocThumbnail>
      <img width="100%" src={iconPath} alt={fileName}></img>
    </DocThumbnail>
  );
};
